import { useState } from 'react';
import { IoTrashOutline } from 'react-icons/io5';

import Error from '../Error';
import Loader from '../Loader';
import Pagination from '../Pagination';
import { tableBackgroundColor } from '../utils/tableBackgroundColor';
import GroupMessagesModal from './modals/GroupMessagesModal';

import { GroupError, GroupMessage } from '@/exceptions/group/group.enum';
import useDeleteGroup from '@/hooks/group/useDeleteGroup';
import { useGetAllGroups } from '@/hooks/group/useGetAllGroups';
import { IGroupResponse } from '@/interfaces/entities/group';
import { notificationService } from '@/services/notification.service';

const GroupTable = () => {
	const deleteGroup = useDeleteGroup();
	const [currentPage, setCurrentPage] = useState(1);
	const { groups, isLoading, isError, error, totalPages } =
		useGetAllGroups(currentPage);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [groupUuid, setGroupUuid] = useState('');

	if (isLoading) {
		return <Loader />;
	}

	if (isError) {
		return <Error message={error ? error.message : GroupError.GROUPS_ERROR} />;
	}

	const handleDelete = (e: any, uuid: string) => {
		e.stopPropagation();
		notificationService.confirm(GroupMessage.DELETE_GROUP_MESSAGE, () => {
			deleteGroup.mutate(uuid);
		});
	};

	const handleGroupMessagesModal = (uuid: string) => {
		setGroupUuid(uuid);
		setIsModalOpen(true);
	};

	const closeGroupMessagesModal = () => {
		setIsModalOpen(false);
	};

	return (
		<div className="px-4 sm:px-6 lg:px-8 w-[90%] mb-10">
			<div className="sm:flex sm:items-center">
				<div className="flex justify-between font-bold sm:flex-auto">
					<h1
						data-test="h1-groups"
						className="text-base font-semibold leading-6 text-gray-900"
					>
						Groups
					</h1>
				</div>
			</div>
			<div className="mt-8 flow-root">
				<div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
					<div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
						<table className="min-w-full divide-y divide-gray-300 border-red-300">
							<thead>
								<tr>
									<th
										scope="col"
										className="w-1/5 px-3 py-3.5 text-center text-sm font-semibold text-gray-900 sm:pl-0"
										data-test="th-id"
									>
										ID
									</th>
									<th
										scope="col"
										className="w-1/5 px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-name"
									>
										Name
									</th>
									<th
										scope="col"
										className="w-1/5 px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-group-owner"
									>
										Group Owner
									</th>
									<th
										scope="col"
										className="w-1/5 px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-number-of-users"
									>
										Number of Users
									</th>
									<th
										scope="col"
										className="w-1/5 px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-action"
									>
										Action
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-200">
								{groups.map((group: IGroupResponse, index) => {
									return (
										<>
											<tr
												key={group.id}
												onClick={() => handleGroupMessagesModal(group.uuid)}
												className={`${tableBackgroundColor(
													index,
												)} hover:bg-gray-200 min-h-8 cursor-pointer`}
											>
												<td
													data-test="td-id"
													className="text-center max-w-sm overflow-hidden text-ellipsis whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 w-60 truncate"
												>
													{group.id}
												</td>
												<td
													data-test="td-name"
													className="text-center max-w-md overflow-hidden text-ellipsis whitespace-nowrap px-3 py-4 text-sm text-gray-500 w-40 truncate"
												>
													{group.name}
												</td>
												<td
													data-test="td-group-owner"
													className="text-center max-w-md overflow-hidden text-ellipsis whitespace-nowrap px-3 py-4 text-sm text-gray-500 w-40 truncate"
												>
													{group.groupOwner.firstName}{' '}
													{group.groupOwner.lastName}
												</td>
												<td
													data-test="td-number-of-users"
													className="text-center max-w-md overflow-hidden text-ellipsis whitespace-nowrap px-3 py-4 text-sm text-gray-500 w-40 truncate"
												>
													{group.numberOfUsers}
												</td>
												<td className="flex justify-center   whitespace-nowrap px-3 py-4 max-w-md overflow-hidden text-sm font-medium">
													<IoTrashOutline
														data-test="delete-group-button"
														className="cursor-pointer self-center"
														onClick={(e) => handleDelete(e, group.uuid)}
													/>
												</td>
											</tr>
										</>
									);
								})}
							</tbody>
						</table>
						{isModalOpen && (
							<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
								<div className="bg-white w-1/3 p-3 rounded-xl shadow-lg border-2 border-black content-center">
									<GroupMessagesModal
										closeModal={closeGroupMessagesModal}
										uuid={groupUuid}
									/>
								</div>
							</div>
						)}
						<Pagination
							currentPage={currentPage}
							setCurrentPage={setCurrentPage}
							totalPages={totalPages as number}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default GroupTable;
